import { useEffect, useState } from 'react';
import {
  Button,
  EmptyState,
  ErrorState,
  Footnote,
  Group,
  Icon,
  Notice,
  Row,
  SectionLabel,
} from '@store/ui';
import { api, formatMoney, type OrderSummary } from '../api';
import { haptic, openInvoice } from '../telegram';
import { fillScreen, ListSkeleton, OrderRow, OrderStatus, useAsync } from './shared';

// --- Orders -----------------------------------------------------------------

export function OrdersScreen({ onNavigate }: { onNavigate: (route: string) => void }) {
  const orders = useAsync<{ orders: OrderSummary[] }>(() => api.orders(), []);
  const list = orders.data?.orders ?? [];

  /*
   * The heading is outside the branch on purpose. It used to appear only once
   * the list had loaded, so the screen went skeleton → title-plus-list and
   * everything shifted down a line at the moment the data arrived.
   */
  return (
    <div className="container stack">
      <h1 className="title">Мои заказы</h1>

      {orders.loading ? (
        <ListSkeleton />
      ) : orders.error ? (
        <div style={fillScreen}>
          <ErrorState
            title="Не удалось загрузить заказы"
            text={orders.error ?? undefined}
            onRetry={orders.reload}
          />
        </div>
      ) : list.length ? (
        <Group>
          {list.map((order) => (
            <OrderRow
              key={order.id}
              order={order}
              onOpen={() => onNavigate(`/orders/${order.id}`)}
            />
          ))}
        </Group>
      ) : (
        <div style={fillScreen}>
          <EmptyState
            icon="receipt"
            title="Заказов пока нет"
            text="Оформленные заказы появятся здесь."
            action={<Button onClick={() => onNavigate('/catalog')}>В каталог</Button>}
          />
        </div>
      )}
    </div>
  );
}

/**
 * The states an order can still leave on its own.
 *
 * Everything else is settled: delivered, cancelled, refunded, failed. While the
 * order is in one of these, something outside this screen — a webhook, the
 * fulfilment worker, an operator — may still change it, so the screen watches.
 */
const UNSETTLED: ReadonlySet<string> = new Set([
  'draft',
  'awaiting_payment',
  'payment_processing',
  'paid',
  'fulfillment_processing',
]);

/**
 * The moment the server says it will give up on this order, as a timestamp.
 *
 * `expiresAt` is the API's own deadline for an unpaid order, and the ten
 * minutes behind it is the server's policy, not this screen's. That is the
 * whole reason the number is nowhere in this file: a client counting down from
 * a constant of its own tells a confident lie the day the policy moves, and it
 * tells it right next to the button the customer is deciding whether to press.
 * No field, no countdown — a missing deadline is not an excuse to invent one.
 *
 * `null` too for an order that is settled or unparseable: nothing is going to
 * be cancelled for non-payment, so there is nothing to count.
 */
export function paymentDeadline(
  order: { status: string; expiresAt?: string | null } | null | undefined,
): number | null {
  if (!order?.expiresAt || !UNSETTLED.has(order.status)) return null;

  const at = Date.parse(order.expiresAt);
  return Number.isFinite(at) ? at : null;
}

/**
 * What is left, as «м:сс».
 *
 * Rounded up, so the last second before the deadline reads «0:01» rather than
 * sitting on «0:00» for a whole second while the order is still payable.
 */
export function formatCountdown(remainingMs: number): string {
  const seconds = Math.max(0, Math.ceil(remainingMs / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/**
 * The post-deadline reconciliation window.
 *
 * The clock reaching zero is not the same event as the order being cancelled:
 * the sweep that cancels it is a worker job on a one-minute schedule, so the
 * screen has to keep asking for a while. Seven tries doubling from two seconds
 * spans a little over four minutes — several sweep periods — and then stops,
 * because a screen left open must not become a request loop.
 */
const POST_DEADLINE_TRIES = 6;
const POST_DEADLINE_BASE_MS = 2000;

export function OrderScreen({ orderId }: { orderId: string }) {
  const detail = useAsync(() => api.order(orderId), [orderId]);
  const [content, setContent] = useState<{ content: string; instructions: string | null } | null>(
    null,
  );
  const [revealing, setRevealing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);

  const status = detail.data?.order.status;
  const deadline = paymentDeadline(detail.data?.order);
  const [now, setNow] = useState(() => Date.now());

  const remaining = deadline === null ? null : Math.max(0, deadline - now);
  const expired = remaining !== null && remaining === 0;

  /*
   * The countdown's clock, ticked here and nowhere else.
   *
   * The deadline is a fixed instant the server already sent, so counting down
   * to it needs no requests at all — one interval, one second, arithmetic
   * against a number that was decided elsewhere. Polling for the time would be
   * two hundred requests to be told what this line can work out for free.
   *
   * It stops itself the moment there is nothing left. A timer that keeps firing
   * past its own deadline is a screen counting into negative numbers for as
   * long as the customer leaves it open.
   */
  useEffect(() => {
    if (deadline === null) return;

    /* `now` was seeded when the screen mounted, and the order carrying the
       deadline landed some hundreds of milliseconds after that. Re-read the
       clock before counting, or the first frame is late by the fetch. */
    const started = Date.now();
    setNow(started);
    if (started >= deadline) return;

    const timer = setInterval(() => {
      const tick = Date.now();
      setNow(tick);
      if (tick >= deadline) clearInterval(timer);
    }, 1000);

    return () => clearInterval(timer);
  }, [deadline]);

  /*
   * The screen watches an order that has not settled yet.
   *
   * `product.tsx` sends the customer here after any payment whose outcome it
   * cannot know — a blocked popup, an opener that reports nothing back — and its
   * comment says this screen "polls the real status and is the only thing that
   * knows". It did not. The order was read once on mount and never again, so a
   * payment confirmed by webhook a second later left «Ждёт оплаты» and an
   * «Оплатить» button on screen, which is an invitation to pay twice.
   *
   * Bounded on purpose: a fixed number of tries, then it stops and leaves the
   * pull-to-refresh Telegram already provides. An unbounded timer on a screen a
   * customer may leave open is a request every few seconds for as long as the
   * app is alive.
   *
   * `refresh` and not `reload`: this is a re-read of an order already on
   * screen, so it must not raise `loading` and send the whole render through
   * the skeleton below for a frame. That was the flash.
   */
  useEffect(() => {
    if (!status || !UNSETTLED.has(status)) return;

    /*
     * After the deadline: keep asking until the server agrees it is over.
     *
     * This was one request. The sweep that cancels an expired order is a
     * worker job on its own schedule, so the moment the clock hits zero the
     * server has usually not acted yet — that single read comes back
     * `awaiting_payment`, and with nothing following it the screen sat on a
     * dead order showing a countdown at zero until the customer reloaded.
     *
     * Bounded and backing off, because the alternative failure is a screen
     * left open all evening asking every three seconds. Doubling from two
     * seconds gives seven tries across about four minutes, which covers the
     * sweep's one-minute period several times over without becoming a loop.
     */
    if (expired) {
      let cancelled = false;
      let timer: ReturnType<typeof setTimeout> | undefined;

      const ask = (attempt: number): void => {
        if (cancelled || attempt > POST_DEADLINE_TRIES) return;
        detail.refresh();
        timer = setTimeout(() => ask(attempt + 1), POST_DEADLINE_BASE_MS * 2 ** attempt);
      };

      ask(0);
      return () => {
        cancelled = true;
        if (timer) clearTimeout(timer);
      };
    }

    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      if (attempts > 10) {
        clearInterval(timer);
        return;
      }
      detail.refresh();
    }, 3000);

    return () => clearInterval(timer);
    /* Keyed on the three things that decide whether to watch at all, and on
       nothing that moves every render — a dep that changed each frame would
       clear and restart the timer before it ever fired, so the order would
       never be re-read. `expired` is a boolean and flips exactly once, which
       costs one restart and buys the stop.

       `status` is a dep, so each answer that is still unsettled re-enters this
       effect and starts the backoff afresh — which is what makes the retry a
       retry rather than a single shot. The moment the status becomes terminal
       the guard above returns and every timer is cleared. */
  }, [status, orderId, expired]);

  /**
   * Pays an invoice that already exists, and then says what happened.
   *
   * This used to be `onClick={() => openInvoice(url)}` — fire and forget. Telegram
   * answers with the outcome and the answer was dropped on the floor, so the
   * screen still read «Ждёт оплаты» after a successful payment.
   *
   * `openInvoice` is the right opener and the only one that reports back: inside
   * Telegram a digital order is paid in Stars, and a Stars invoice is a
   * `t.me/$slug` link, which is the only thing this method accepts.
   */
  const pay = async (url: string) => {
    if (paying) return;
    setPaying(true);
    setPayError(null);

    try {
      const outcome = await openInvoice(url);

      if (outcome === 'paid') {
        haptic.success();
      } else if (outcome === 'failed') {
        haptic.error();
        setPayError('Оплата не прошла. Деньги не списаны.');
      }

      // Reloaded whatever the answer was, including «cancelled»: the invoice may
      // have expired while the sheet was open, and the button must not keep
      // offering a link the server has already given up on.
      detail.reload();
    } finally {
      setPaying(false);
    }
  };

  const reveal = async () => {
    setRevealing(true);
    try {
      const result = await api.orderContent(orderId);
      setContent(result);
      haptic.success();
    } catch {
      haptic.error();
    } finally {
      setRevealing(false);
    }
  };

  const copy = async () => {
    if (!content) return;
    await navigator.clipboard?.writeText(content.content).catch(() => {});
    setCopied(true);
    haptic.impact('light');
    setTimeout(() => setCopied(false), 2000);
  };

  if (detail.loading) {
    return (
      <div className="container">
        <ListSkeleton rows={2} />
      </div>
    );
  }

  if (detail.error || !detail.data) {
    return (
      <div className="container">
        <div style={fillScreen}>
          <ErrorState
            title="Заказ не найден"
            text={detail.error ?? undefined}
            onRetry={detail.reload}
          />
        </div>
      </div>
    );
  }

  const { order, items, payment } = detail.data;

  return (
    <div className="container stack">
      <header>
        <h1 className="title">Заказ {order.orderNumber}</h1>
        <div style={{ marginTop: 'var(--space-2)' }}>
          <OrderStatus status={order.status} label={order.statusLabel} />
        </div>
        {/* Under the pill and quiet, because it qualifies the status rather
            than competing with it. `remaining` is null unless the server sent a
            deadline, so a store that does not expire orders shows nothing
            here — which is the honest answer, not a timer at zero.

            `.caption` and not `.footnote`: a footnote carries the inset that
            belongs to a grouped block, and there is no group here — it would
            sit sixteen pixels right of the title and the pill it explains. */}
        {/*
         * Three things this line can be saying, and the third is the honest
         * one nobody wrote before.
         *
         * The clock reaching zero does not cancel the order — a worker sweep
         * does, on its own schedule — so between those two moments the status
         * pill above still reads «Ждёт оплаты» while the payment button has
         * already gone. Saying only «время истекло» there leaves the customer
         * looking at a contradiction and wondering whether to wait. Saying
         * that the cancellation is being confirmed is both true and the answer
         * to what they are about to ask.
         */}
        {remaining !== null ? (
          <div className="caption" style={{ marginTop: 'var(--space-2)' }}>
            {remaining > 0
              ? `Осталось на оплату ${formatCountdown(remaining)}`
              : 'Время на оплату истекло. Отменяем заказ…'}
          </div>
        ) : null}
      </header>

      <section>
        <SectionLabel>Состав</SectionLabel>
        <Group>
          {items.map((item, index) => (
            <Row
              key={index}
              title={item.title}
              {...(item.variantTitle ? { subtitle: item.variantTitle } : {})}
              trailing={<span className="price">{formatMoney(item.lineTotal)}</span>}
            />
          ))}
          <Row
            title={<strong>Итого</strong>}
            trailing={<span className="price--lg">{formatMoney(order.totalAmount)}</span>}
          />
        </Group>
        {order.status === 'manual_review' ? (
          /* The pill in the header already carries the warning colour. Saying
             it again inside a coloured box says one thing twice and louder;
             under the order, quietly, it reads as the note it is. */
          <Footnote>
            Заказ проверяет оператор. Мы напишем в чат, как только всё будет готово.
          </Footnote>
        ) : null}
      </section>

      {/*
       * The button goes the moment the clock does.
       *
       * It used to be gated on `payment.paymentUrl` alone, and that URL long
       * outlives the window: at zero the invoice is dead but the row still
       * carries it, so the screen kept offering a payment the server would
       * refuse — under a countdown reading «Время на оплату истекло». Paying
       * into an expired invoice is the one outcome nobody can undo cheaply.
       */}
      {payment?.paymentUrl && !expired ? (
        <div className="stack stack--tight">
          <Button block loading={paying} onClick={() => void pay(payment.paymentUrl!)}>
            Оплатить
          </Button>
          {/* The server's own sentence about a refusal, next to the button that
              caused it — not a generic one, and not somewhere it can be scrolled
              away from. */}
          {payError ? <Notice tone="warning">{payError}</Notice> : null}
        </div>
      ) : null}

      {order.status === 'delivered' ? (
        content ? (
          <>
            <section className="stack stack--tight">
              <SectionLabel>Ваш товар</SectionLabel>
              {/* Selectable in full on one tap: a customer copying a licence key
                  by dragging across a truncated line gets half a key. */}
              <p className="secret">{content.content}</p>
              <Button variant="secondary" block onClick={copy}>
                <Icon name={copied ? 'check' : 'copy'} size={16} />
                {copied ? 'Скопировано' : 'Копировать'}
              </Button>
            </section>

            {/* Its own section: the instruction label used to sit twelve pixels
                under the copy button, inside the same tight stack, and read as
                a caption for it. */}
            {content.instructions ? (
              <section>
                <SectionLabel>Инструкция</SectionLabel>
                <Group padded>
                  <p className="text">{content.instructions}</p>
                </Group>
              </section>
            ) : null}
          </>
        ) : (
          <Button block onClick={reveal} loading={revealing}>
            <Icon name="key" size={18} />
            Показать товар
          </Button>
        )
      ) : null}
    </div>
  );
}
